import * as React from 'react';
import CalendarAllDayWell from './CalendarAllDayWell';
import CalendarDayConfig from '../interfaces/CalendarDayConfig';
import CalendarDayContent from '../interfaces/CalendarDayContent';
import CalendarGrid from './CalendarGrid';
import FocusableSurfaceComponent from './FocusableSurfaceComponent';
import SurfaceConstants from '../styles/SurfaceConstants';
import SurfaceStore from '../interfaces/SurfaceStore';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import toPixelString from '../utils/toPixelString';
import { attachBehaviors } from './BehaviorsHelper';
import { DEFAULT_HEADER_WIDTH } from '../utils/constants';
import { logMountingPerformance } from 'owa-analytics';
import { observer } from 'mobx-react';

let styles = require('./CalendarDayList.scss');
let classNames = require('classnames/bind').bind(styles);

const MIN_BOUNDS_PADDING = 16;
/**
 * Props for calendar day list control
 */
export interface CalendarDayListProps {
    config: CalendarDayConfig;
    store: SurfaceStore;
}
interface CalendarGridBound {
    element: HTMLElement;
    bounds: ClientRect;
}
/**
 * CalendarDayList shows calendar day grids with the ability to show calendar views side by side for each selected calendar.
 */
@logMountingPerformance('CalendarDayList')
@observer
export default class CalendarDayList extends React.Component<CalendarDayListProps, {}> {
    private gridViewRefs: CalendarGridBound[];
    private inDayScrollRef: CalendarGridBound;
    private gridHeadersScrollRef: HTMLElement;
    private allDayScrollRef: HTMLElement;

    render() {
        // clear the existing split Day ref, so we can rebuild new list
        this.gridViewRefs = [];

        let { config } = this.props;

        if (!config.calendarDayContentConfigProvider) {
            return null;
        }

        let calendarContentConfigs = config.calendarDayContentConfigProvider();
        if (!calendarContentConfigs || calendarContentConfigs.length <= 0) {
            return null;
        }

        let headerWidth = config.timeHeaderWidth || DEFAULT_HEADER_WIDTH;
        let headerPixelString = toPixelString(headerWidth);

        let itemsPanelStyle: React.CSSProperties = {
            height: toPixelString(
                SurfaceConstants.HourRowHeight *
                    (TimeConstants.MillisecondsInOneDay / TimeConstants.MillisecondsIn1Hour)
            ),
        };

        let allDayMinWidth = config.minWidthProvider && {
            minWidth: config.minWidthProvider() + 'px',
        };

        // track the weather icon to be shown only once and not for all calendars
        let showedHeaderAddOnOnce: boolean = false;

        let calendarGridStyle = config.minWidthProvider && {
            minWidth: config.minWidthProvider() + 'px',
        };

        return (
            <FocusableSurfaceComponent
                tabOrderProvider={() =>
                    this.mergeTabs(
                        calendarContentConfigs.map(config => {
                            return config.content;
                        })
                    )
                }
                setFocusedElement={config.setFocusedElement}
                ref={config.surfaceComponent}>
                <div className={styles.page}>
                    <div className={styles.topPanel}>
                        {attachBehaviors(
                            <div
                                style={{ width: headerPixelString, display: 'flex', flexShrink: 0 }}
                            />,
                            calendarContentConfigs[0].timeStripHeaderBehaviors
                        )}
                        <div
                            className={styles.scrollableContainer}
                            onScroll={this.allDayPanelScroll}
                            ref={this.allDayScrollRefFunction}>
                            {calendarContentConfigs.map(contentConfig => {
                                let hideHeaderAddOn = showedHeaderAddOnOnce;
                                showedHeaderAddOnOnce = true;
                                let providerContent = contentConfig.content;
                                return (
                                    <div className={styles.topContainer} style={allDayMinWidth}>
                                        {!config.hideTopHeaders &&
                                            calendarContentConfigs.length > 1 &&
                                            contentConfig.contentDescription && (
                                                <div className={styles.contentHeader}>
                                                    {contentConfig.contentDescription()}
                                                </div>
                                            )}
                                        {!config.hideTopHeaders &&
                                            attachBehaviors(
                                                <div
                                                    className={classNames(styles.header, {
                                                        stackedHeader:
                                                            calendarContentConfigs.length > 1,
                                                    })}>
                                                    {providerContent.headers(hideHeaderAddOn)}
                                                </div>,
                                                contentConfig.allDayBehaviors
                                            )}
                                        <CalendarAllDayWell
                                            className={styles.allDayPanel}
                                            config={contentConfig}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {/* In-Day panel */}
                    <div className={classNames(styles.inDayPanel)}>
                        <div
                            className={styles.gridHeadersScrollContainer}
                            ref={this.gridHeadersScrollRefFunction}>
                            {attachBehaviors(
                                <div className={styles.timeContainer} />,
                                calendarContentConfigs[0].timeStripBehaviors
                            )}
                        </div>
                        {attachBehaviors(
                            <div
                                className={classNames(styles.scrollContainer, 'customScrollBar')}
                                onScroll={this.inDayPanelScroll}
                                data-is-scrollable={true}
                                ref={this.inDayScrollRefFunction}>
                                <div className={styles.growFlexBox} style={itemsPanelStyle}>
                                    {calendarContentConfigs.map(contentAndBehaviorConfig => (
                                        <div
                                            className={styles.renderPanel}
                                            style={calendarGridStyle}
                                            ref={this.gridViewRefsFunction}>
                                            <CalendarGrid
                                                contentConfig={contentAndBehaviorConfig}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>,
                            calendarContentConfigs[0].inDayBehaviors || []
                        )}
                    </div>
                </div>
            </FocusableSurfaceComponent>
        );
    }

    private inDayPanelScroll = e => {
        const container: HTMLDivElement = e.currentTarget;
        this.gridHeadersScrollRef && (this.gridHeadersScrollRef.scrollTop = container.scrollTop);
        this.allDayScrollRef && (this.allDayScrollRef.scrollLeft = container.scrollLeft);
    };

    private allDayPanelScroll = e => {
        const container: HTMLDivElement = e.currentTarget;
        // move the inDayPanel along with the allDayPanel to keep them aligned
        this.inDayScrollRef && (this.inDayScrollRef.element.scrollLeft = container.scrollLeft);
    };

    private mergeTabs(contents: CalendarDayContent[]): string[] {
        let results = [];
        // When merging tabs we have to remove surface headers from all except first content stream
        contents.forEach((content, index) =>
            results.push(
                ...content
                    .tabOrderProvider()
                    .filter(tabId => index == 0 || tabId.search('surfaceHeader_') < 0)
            )
        );
        return results;
    }

    public getPopupBounds = (anchorElement: HTMLElement) => {
        if (this.gridViewRefs && this.gridViewRefs.length > 0 && this.inDayScrollRef) {
            let currentRef = this.gridViewRefs.filter(a => a && a.element.contains(anchorElement));
            if (currentRef && currentRef.length > 0) {
                let horizontalBounds = currentRef[0].bounds;
                let scrollLeft = this.inDayScrollRef.element.scrollLeft;
                let verticalBounds = this.inDayScrollRef.bounds;
                if (horizontalBounds && verticalBounds) {
                    return {
                        top: verticalBounds.top + MIN_BOUNDS_PADDING,
                        bottom: verticalBounds.bottom - MIN_BOUNDS_PADDING,
                        height: verticalBounds.height - MIN_BOUNDS_PADDING * 2,
                        left: horizontalBounds.left - scrollLeft + MIN_BOUNDS_PADDING,
                        right: horizontalBounds.right - scrollLeft - MIN_BOUNDS_PADDING,
                        width: horizontalBounds.width - MIN_BOUNDS_PADDING * 2,
                    };
                }
            }
        }

        // if anchor is not in the component, we have no constraints
        return null;
    };

    private gridViewRefsFunction = (element: HTMLElement) => {
        if (element) {
            setTimeout(() => {
                // build a list of grid refs per calendar, so that we can anchor peeks
                // based on which calendar grid the clicked item belongs to.
                this.gridViewRefs.push({
                    element: element,
                    bounds: element.getBoundingClientRect(),
                });
            }, 0);
        }
    };

    public inDayScrollRefFunction = (element: HTMLElement) => {
        if (element) {
            setTimeout(() => {
                this.inDayScrollRef = {
                    element: element,
                    bounds: element.getBoundingClientRect(),
                };
            }, 0);
        }
    };

    private gridHeadersScrollRefFunction = (element: HTMLElement) => {
        this.gridHeadersScrollRef = element;
    };

    private allDayScrollRefFunction = (element: HTMLElement) => {
        this.allDayScrollRef = element;
    };
}
